import { GroupTemplate } from '../../../../../types';

export interface GroupTemplateFormData extends Partial<GroupTemplate> {
  allowWebPosting: boolean;
  primaryLanguage:
    | 'Arabic'
    | 'Bengali'
    | 'Bulgarian'
    | 'Catalan'
    | 'Chinese_Simplified'
    | 'Chinese_Traditional'
    | 'Croatian'
    | 'Czech'
    | 'Danish'
    | 'Dutch'
    | 'English_United_States'
    | 'English_United_Kingdom'
    | 'Estonian'
    | 'Finnish'
    | 'French'
    | 'German'
    | 'Greek'
    | 'Gujarati'
    | 'Hebrew'
    | 'Hindi'
    | 'Hungarian'
    | 'Icelandic'
    | 'Indonesian'
    | 'Italian'
    | 'Japanese'
    | 'Kannada'
    | 'Korean'
    | 'Latvian'
    | 'Lithuanian'
    | 'Malay'
    | 'Malayalam'
    | 'Marathi'
    | 'Norwegian'
    | 'Oriya'
    | 'Persian'
    | 'Polish'
    | 'Portuguese_Brazil'
    | 'Portuguese_Portugal'
    | 'Romanian'
    | 'Russian'
    | 'Serbian'
    | 'Slovak'
    | 'Slovenian'
    | 'Spanish'
    | 'Swedish'
    | 'Tagalog'
    | 'Tamil'
    | 'Telugu'
    | 'Thai'
    | 'Turkish'
    | 'Ukrainian'
    | 'Urdu'
    | 'Vietnamese';
  includeInGlobalAddressList: boolean;
  isArchived: boolean;
  membersCanPostAsTheGroup: boolean;
  messageModerationLevel:
    | 'MODERATE_ALL_MESSAGES'
    | 'MODERATE_NEW_MEMBERS'
    | 'MODERATE_NON_MEMBERS'
    | 'MODERATE_NONE';
  sendMessageDenyNotification: boolean;
  spamModerationLevel: 'MODERATE' | 'SILENTLY_MODERATE' | 'REJECT' | 'ALLOW';
  replyTo: string;
  defaultMessageDenyNotificationText: string;
  whoCanViewGroup:
    | 'ANYONE_CAN_VIEW'
    | 'ALL_MANAGERS_CAN_VIEW'
    | 'ALL_MEMBERS_CAN_VIEW'
    | 'ALL_IN_DOMAIN_CAN_VIEW'
    | 'ALL_OWNERS_CAN_VIEW';
  whoCanPostMessage:
    | 'NONE_CAN_POST'
    | 'ALL_MANAGERS_CAN_POST'
    | 'ALL_MEMBERS_CAN_POST'
    | 'ALL_IN_DOMAIN_CAN_POST'
    | 'ALL_OWNERS_CAN_POST'
    | 'ANYONE_CAN_POST';
  whoCanViewMembership:
    | 'ALL_MANAGERS_CAN_VIEW'
    | 'ALL_MEMBERS_CAN_VIEW'
    | 'ALL_IN_DOMAIN_CAN_VIEW'
    | 'ALL_OWNERS_CAN_VIEW';
  whoCanModerateMembers:
    | 'ALL_MEMBERS'
    | 'OWNERS_AND_MANAGERS'
    | 'OWNERS_ONLY'
    | 'NONE';
  whoCanModerateContent:
    | 'ALL_MEMBERS'
    | 'OWNERS_AND_MANAGERS'
    | 'OWNERS_ONLY'
    | 'NONE';
  whoCanAssistContent:
    | 'ALL_MEMBERS'
    | 'OWNERS_AND_MANAGERS'
    | 'OWNERS_ONLY'
    | 'MANAGERS_ONLY'
    | 'NONE';
  allowExternalMembers: boolean;
  whoCanJoin:
    | 'ANYONE_CAN_JOIN'
    | 'ALL_IN_DOMAIN_CAN_JOIN'
    | 'CAN_REQUEST_TO_JOIN'
    | 'INVITED_CAN_JOIN';
  whoCanLeaveGroup:
    | 'ALL_MANAGERS_CAN_LEAVE'
    | 'ALL_MEMBERS_CAN_LEAVE'
    | 'ALL_OWNERS_CAN_LEAVE'
    | 'NONE_CAN_LEAVE';
  whoCanContactOwner:
    | 'ALL_MANAGERS_CAN_CONTACT'
    | 'ALL_MEMBERS_CAN_CONTACT'
    | 'ALL_IN_DOMAIN_CAN_CONTACT'
    | 'ANYONE_CAN_CONTACT';
  whoCanDiscoverGroup:
    | 'ANYONE_CAN_DISCOVER'
    | 'ALL_IN_DOMAIN_CAN_DISCOVER'
    | 'ALL_MEMBERS_CAN_DISCOVER';
  archiveOnly: boolean;
  groupTemplateName: string;
  groupTemplateDesc: string;
  customReplyTo: string;
  createdDate: number;
  securityQuery?: string;
}

export const groupTemplateFormDefaultValues: GroupTemplateFormData = {
  allowWebPosting: false,
  primaryLanguage: 'English_United_States',
  includeInGlobalAddressList: false,
  isArchived: false,
  membersCanPostAsTheGroup: false,
  messageModerationLevel: 'MODERATE_ALL_MESSAGES',
  sendMessageDenyNotification: false,
  spamModerationLevel: 'SILENTLY_MODERATE',
  replyTo: 'REPLY_TO_MANAGERS',
  defaultMessageDenyNotificationText: '',
  whoCanViewGroup: 'ALL_IN_DOMAIN_CAN_VIEW',
  whoCanPostMessage: 'ALL_MANAGERS_CAN_POST',
  whoCanViewMembership: 'ALL_MEMBERS_CAN_VIEW',
  whoCanModerateMembers: 'OWNERS_AND_MANAGERS',
  whoCanModerateContent: 'OWNERS_AND_MANAGERS',
  whoCanAssistContent: 'OWNERS_AND_MANAGERS',
  whoCanLeaveGroup: 'NONE_CAN_LEAVE',
  whoCanContactOwner: 'ALL_MEMBERS_CAN_CONTACT',
  whoCanDiscoverGroup: 'ALL_IN_DOMAIN_CAN_DISCOVER',
  allowExternalMembers: false,
  whoCanJoin: 'ALL_IN_DOMAIN_CAN_JOIN',
  archiveOnly: false,
  groupTemplateName: '',
  groupTemplateDesc: '',
  customReplyTo: '',
  createdDate: 0,
  securityQuery: '',
};

// Stored templates can have null fields (e.g. older templates); fall back to
// the defaults so the form schema accepts them. Identity fields (id,
// keyString, createdBy) are kept so the edit is saved to the same template.
export function toGroupTemplateFormValues(
  template: GroupTemplate
): GroupTemplateFormData {
  const filledFields = Object.fromEntries(
    Object.entries(groupTemplateFormDefaultValues).map(([key, fallback]) => [
      key,
      template[key as keyof GroupTemplate] ?? fallback,
    ])
  );
  return { ...template, ...filledFields } as GroupTemplateFormData;
}

export function toGroupTemplatePayload(
  data: GroupTemplateFormData
): GroupTemplate {
  return { ...data } as GroupTemplate;
}
